import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend
);

export interface NumericFieldDataPoint {
  rangeLabel: string;
  averageMood: number;
  count: number;
}

export interface NumericFieldData {
  id: string;
  name: string;
  color: string;
  dataPoints: NumericFieldDataPoint[];
}

export interface NumericDependenciesChartProps {
  /** Array of numeric field data with their mood correlations */
  fields: NumericFieldData[];
  /** Labels for the chart */
  labels?: {
    title?: string;
    yAxisLabel?: string;
    xAxisLabel?: string;
    noDataMessage?: string;
  };
  /** Height of the chart container */
  height?: number;
}

// Generate a consistent color for a field based on its ID
function generateFieldColor(index: number): string {
  const colors = [
    '#667eea', // primary blue-purple
    '#28a745', // green
    '#fd7e14', // orange
    '#dc3545', // red
    '#20c997', // teal
    '#6f42c1', // purple
    '#e83e8c', // pink
    '#17a2b8', // cyan
    '#ffc107', // yellow
    '#6c757d', // gray
  ];
  return colors[index % colors.length];
}

export function NumericDependenciesChart({
  fields,
  labels = {},
  height = 400,
}: NumericDependenciesChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(() => {
    // Select first 3 fields by default, or all if less than 3
    const initialSelected = new Set<string>();
    fields.slice(0, 3).forEach(f => initialSelected.add(f.id));
    return initialSelected;
  });

  const {
    title = 'Mood vs Numeric Field Values',
    yAxisLabel = 'Average Mood (%)',
    xAxisLabel = 'Field Value Range (%)',
    noDataMessage = 'No numeric field data available',
  } = labels;

  // Get all unique range labels (X-axis)
  const rangeLabels = ['0-20%', '20-40%', '40-60%', '60-80%', '80-100%'];

  useEffect(() => {
    if (!chartRef.current || fields.length === 0) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Filter and prepare datasets for selected fields
    const datasets = fields
      .filter(field => selectedFields.has(field.id))
      .map((field, index) => {
        // Map data points to the range labels
        const data = rangeLabels.map(label => {
          const point = field.dataPoints.find(dp => dp.rangeLabel === label);
          return point ? point.averageMood : null;
        });

        return {
          label: field.name,
          data,
          borderColor: field.color || generateFieldColor(index),
          backgroundColor: (field.color || generateFieldColor(index)) + '20',
          borderWidth: 2,
          tension: 0.3,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
          spanGaps: true, // Connect points even when some data is missing
        };
      });

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: rangeLabels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: !!title,
            text: title,
            font: {
              size: 16,
              weight: 'bold',
            },
          },
          legend: {
            display: true,
            position: 'top',
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const field = fields.find(f => f.name === context.dataset.label);
                if (!field) return context.dataset.label || '';

                const rangeIndex = context.dataIndex;
                const dataPoint = field.dataPoints.find(
                  dp => dp.rangeLabel === rangeLabels[rangeIndex]
                );
                const count = dataPoint?.count ?? 0;

                return `${context.dataset.label}: ${context.parsed.y?.toFixed(1)}% mood (${count} days)`;
              },
            },
          },
        },
        scales: {
          y: {
            min: 0,
            max: 100,
            title: {
              display: true,
              text: yAxisLabel,
            },
            ticks: {
              callback: (value) => `${value}%`,
            },
          },
          x: {
            title: {
              display: true,
              text: xAxisLabel,
            },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [fields, selectedFields, title, yAxisLabel, xAxisLabel]);

  const toggleField = (fieldId: string) => {
    setSelectedFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fieldId)) {
        newSet.delete(fieldId);
      } else {
        newSet.add(fieldId);
      }
      return newSet;
    });
  };

  if (fields.length === 0) {
    return (
      <div className="numeric-dependencies-chart__empty">
        <p>{noDataMessage}</p>
      </div>
    );
  }

  return (
    <div className="numeric-dependencies-chart">
      {/* Field selection checkboxes */}
      <div className="numeric-dependencies-chart__field-selector">
        {fields.map((field, index) => (
          <label
            key={field.id}
            className="numeric-dependencies-chart__field-checkbox"
            style={{
              '--field-color': field.color || generateFieldColor(index),
            } as React.CSSProperties}
          >
            <input
              type="checkbox"
              checked={selectedFields.has(field.id)}
              onChange={() => toggleField(field.id)}
            />
            <span
              className="numeric-dependencies-chart__field-color"
              style={{ backgroundColor: field.color || generateFieldColor(index) }}
            />
            <span className="numeric-dependencies-chart__field-name">{field.name}</span>
          </label>
        ))}
      </div>

      {/* Chart canvas */}
      <div className="numeric-dependencies-chart__canvas-container" style={{ height }}>
        <canvas ref={chartRef} />
      </div>
    </div>
  );
}

/**
 * Mount function for rendering from Rails
 */
export function mountNumericDependenciesChart(
  container: HTMLElement,
  props: NumericDependenciesChartProps
) {
  const root = createRoot(container);
  root.render(<NumericDependenciesChart {...props} />);
  return root;
}

export default NumericDependenciesChart;
